import React from "react";
import { X, Camera, Image as ImageIcon, Plus, ChevronDown } from "lucide-react";

/**
 * Collapsible add/edit form for a certification.
 * Pure UI — state is owned by the parent CertModal.
 */
export default function CertForm({
  form,
  setForm,
  certTypes,
  expanded,
  editing,
  saving,
  onToggle,
  onCancelEdit,
  onSubmit,
}) {
  const isCustom = form.cert_type === "__custom__";

  return (
    <div className="border-2 border-foreground dark:border-safety-yellow mb-6">
      <button
        type="button"
        data-testid="cert-add-toggle"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls="cert-form-body"
        className={`w-full min-h-[56px] px-4 inline-flex items-center justify-between gap-3 font-black uppercase tracking-wider text-sm transition-colors ${
          expanded ? "bg-safety-yellow text-black" : "bg-transparent text-foreground hover:bg-safety-yellow hover:text-black"
        }`}
      >
        <span className="inline-flex items-center gap-2">
          <Plus className={`w-5 h-5 transition-transform duration-200 ${expanded ? "rotate-45" : ""}`} strokeWidth={3} />
          {editing ? "Update Certification" : "Add Certification"}
        </span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} strokeWidth={3} />
      </button>

      {expanded && (
        <form id="cert-form-body" onSubmit={onSubmit}
              className="space-y-4 p-4 border-t-2 border-foreground dark:border-safety-yellow">
          {editing && (
            <div className="flex items-center justify-end">
              <button type="button" onClick={onCancelEdit} data-testid="cert-cancel-edit"
                      className="text-xs font-bold uppercase tracking-wider underline decoration-safety-yellow underline-offset-4 hover:text-safety-yellow">
                Cancel edit
              </button>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Certification Title</label>
            <select
              data-testid="cert-type" required className="industrial-input"
              value={form.cert_type}
              onChange={(e) => setForm({
                ...form,
                cert_type: e.target.value,
                custom_name: e.target.value === "__custom__" ? form.custom_name : "",
              })}
            >
              <option value="">Select type...</option>
              {certTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              <option value="__custom__">Custom...</option>
            </select>
            {isCustom && (
              <input data-testid="cert-custom" required autoFocus
                     placeholder="Enter Custom Certification Name"
                     className="industrial-input mt-2"
                     value={form.custom_name}
                     onChange={(e) => setForm({ ...form, custom_name: e.target.value })} />
            )}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Expiration Date</label>
            <input data-testid="cert-exp" type="date" required className="industrial-input"
                   value={form.expiration_date}
                   onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Issuer (optional)</label>
            <input data-testid="cert-issuer" className="industrial-input" value={form.issuer}
                   onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="e.g. OSHA" />
          </div>

          <PhotoAttachment form={form} setForm={setForm} />

          <button data-testid="cert-add-btn" disabled={saving} type="submit" className="btn-primary w-full">
            {saving ? "Saving..." : (editing ? "Save Changes" : "Save Certification")}
          </button>
        </form>
      )}
    </div>
  );
}

function PhotoAttachment({ form, setForm }) {
  if (form.photo) {
    return (
      <div className="border-2 border-safety-yellow bg-safety-yellow/10 px-4 min-h-[48px] flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 truncate">
          <ImageIcon className="w-4 h-4 text-safety-yellow flex-shrink-0" /> Photo Attached · {form.photo.name}
        </span>
        <button type="button" data-testid="cert-photo-remove"
                onClick={() => setForm({ ...form, photo: null })}
                className="h-8 w-8 inline-flex items-center justify-center hover:bg-safety-yellow hover:text-black flex-shrink-0"
                aria-label="Remove photo">
          <X className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>
    );
  }
  return (
    <label data-testid="cert-photo-attach"
           className="cursor-pointer w-full inline-flex items-center justify-center gap-2 bg-transparent border-2 border-dashed border-foreground/50 dark:border-safety-yellow/60 px-4 min-h-[48px] text-xs font-bold uppercase tracking-wider hover:bg-safety-yellow/10 hover:border-safety-yellow transition-colors">
      <Camera className="w-4 h-4" strokeWidth={2.5} />
      Attach Photo (Optional)
      <input type="file" accept="image/*" className="hidden"
             onChange={(e) => setForm({ ...form, photo: e.target.files?.[0] || null })} />
    </label>
  );
}
